import { apiClient } from '../api/client';
import { PaymentMethod } from '@/types';

export interface PaymentProvider {
  provider: string;
  displayName: string;
  methods: string[];
  /**
   * Credenciais publicáveis do SDK de tokenização, quando o gateway as usa.
   *
   * Vêm de um endpoint público de propósito: o SDK roda no navegador e precisa
   * delas. Nada aqui autoriza cobrança — isso continua do lado do servidor.
   */
  tokenization?: {
    merchantCredential: string;
    transparentToken: string;
  };
  /**
   * Como o gateway apresenta o pagamento, quando ele tem modos.
   *
   * Hoje só o PicPay usa. A vitrine não decide nada por este campo — quem
   * decide é o `presentation` que volta do checkout — mas ele deixa o estado
   * legível para quem for depurar por que o cartão apareceu ou não.
   */
  checkoutMode?: 'TRANSPARENT' | 'STANDARD' | 'LIGHTBOX';
}

export interface CreateOrderResponse {
  orderId: string;
  status: string;
  total: number;
  items: Array<{
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  createdAt: string;
}

export interface CreateOrderRequest {
  couponCode?: string;
  creditsToUse?: number;
  /** Total visto na tela (reais, antes dos créditos). O servidor recusa se mudou. */
  expectedTotal?: number;
}

export interface CheckoutResponse {
  paymentId: string;
  /**
   * O que fazer com o resultado, decidido pelo gateway.
   *
   * `REDIRECT` leva o jogador para fora, `IFRAME` abre sobre a loja, `QRCODE`
   * mostra o código no modal, `NONE` já resolveu (cartão no transparente).
   */
  presentation?: 'REDIRECT' | 'IFRAME' | 'QRCODE' | 'NONE';
  checkoutUrl?: string;
  qrCode?: string;
  qrCodeBase64?: string;
  method: string;
  provider: string;
  amount: number;
  status: string;
}

export interface CreateCheckoutRequest {
  orderId: string;
  method: PaymentMethod;
  provider: string;
  /** CPF/CNPJ do pagador. Exigido por gateways que o pedem, como o PicPay. */
  buyerDocument?: string;
  /** Token de uso único do cartão, gerado no navegador. Nunca o cartão. */
  cardToken?: string;
  installments?: number;
  successUrl?: string;
  failureUrl?: string;
  pendingUrl?: string;
}

export interface PaymentStatus {
  id: string;
  status: string;
  method: string;
  provider: string;
  amount: number;
  createdAt: string;
  paidAt?: string;
}

class PaymentService {
  /**
   * Create order from cart
   * @param domain - Store domain
   * @param data - Order data
   */
  async createOrderFromCart(
    domain: string,
    data: CreateOrderRequest
  ): Promise<CreateOrderResponse> {
    try {
      const response = await apiClient.post<CreateOrderResponse>(
        `/public/orders?domain=${encodeURIComponent(domain)}`,
        data
      );
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  /**
   * Get available payment methods for a store
   * @param domain - Store domain (e.g., 'mystore.hystore.com.br')
   */
  async getPaymentMethods(domain: string): Promise<PaymentProvider[]> {
    try {
      const response = await apiClient.get<{ providers: PaymentProvider[] }>(
        `/public/payment-methods?domain=${encodeURIComponent(domain)}`
      );
      return response.providers;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  }

  /**
   * Create a payment checkout
   * @param domain - Store domain
   * @param data - Checkout data
   */
  async createCheckout(
    domain: string,
    data: CreateCheckoutRequest
  ): Promise<CheckoutResponse> {
    try {
      const response = await apiClient.post<CheckoutResponse>(
        `/public/checkout?domain=${encodeURIComponent(domain)}`,
        data
      );
      return response;
    } catch (error) {
      console.error('Error creating checkout:', error);
      throw error;
    }
  }

  /**
   * Get payment status by payment ID
   * @param paymentId - Payment ID
   * @param domain - Store domain (required for tenant validation)
   */
  async getPaymentStatus(paymentId: string, domain: string): Promise<PaymentStatus> {
    try {
      const response = await apiClient.get<PaymentStatus>(
        `/public/payment/${paymentId}?domain=${encodeURIComponent(domain)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching payment status:', error);
      throw error;
    }
  }

  /**
   * Get payment status by order ID
   */
  async getPaymentStatusByOrderId(orderId: string, domain: string): Promise<{ payment: PaymentStatus; orderStatus?: string }> {
    try {
      const response = await apiClient.get<{ payment: PaymentStatus; orderStatus?: string }>(
        `/public/payment/order/${orderId}?domain=${encodeURIComponent(domain)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching payment status by order ID:', error);
      throw error;
    }
  }
}

export const paymentService = new PaymentService();
